// UnreachableConfigPanel — settings panel for configuring unreachable (no-answer) retry policy.
// Used in per-workflow settings pages (CF, SC, ST, CS).

import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import {
  Banner, BlockStack, Card, InlineStack,
  Select, Spinner, Text, TextField,
} from '@shopify/polaris'
import { settingsApi } from '../../../sdk/settings.api'
import { getShop }     from '../../../sdk/shop'
import type { UnreachableConfig, UnreachableWorkflow } from '../../../sdk/settings.api'

type Props = { workflow: UnreachableWorkflow }
export type UnreachableConfigPanelRef = { save: () => Promise<void> }

const EXHAUSTION_OPTIONS = [
  { label: 'Hold order', value: 'hold' },
  { label: 'Cancel order', value: 'cancel' },
  { label: 'Move to next workflow', value: 'next_workflow' },
]

export const UnreachableConfigPanel = forwardRef<UnreachableConfigPanelRef, Props>(
  function UnreachableConfigPanel({ workflow }, ref) {
  const shop = getShop() ?? ''

  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [config,  setConfig]  = useState<UnreachableConfig>({
    days: 3,
    attemptsPerDay: [3, 2, 1],
    afterExhaustion: 'hold',
  })

  const load = useCallback(async () => {
    if (!shop) return
    setLoading(true)
    setError(null)
    try {
      const data = await settingsApi.getUnreachableConfig(shop, workflow)
      setConfig(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unreachable config')
    } finally {
      setLoading(false)
    }
  }, [shop, workflow])

  useEffect(() => { load() }, [load])

  useImperativeHandle(ref, () => ({
    async save() {
      setError(null)
      try {
        await settingsApi.setUnreachableConfig(shop, workflow, config)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save unreachable policy')
        throw err
      }
    },
  }), [shop, workflow, config])

  function updateAttemptsPerDay(dayIdx: number, value: string) {
    const num = parseInt(value, 10)
    if (isNaN(num) || num < 0) return
    setConfig(prev => {
      const arr = [...prev.attemptsPerDay]
      arr[dayIdx] = num
      return { ...prev, attemptsPerDay: arr }
    })
  }

  function syncDaysCount(newDays: number) {
    setConfig(prev => {
      const arr = [...prev.attemptsPerDay]
      // Grow or shrink the attemptsPerDay array to match days count
      while (arr.length < newDays) arr.push(1)
      const trimmed = arr.slice(0, newDays)
      return { ...prev, days: newDays, attemptsPerDay: trimmed }
    })
  }

  if (loading) {
    return (
      <Card>
        <InlineStack align="center"><Spinner accessibilityLabel="Loading unreachable config" /></InlineStack>
      </Card>
    )
  }

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h3" variant="headingMd">Unreachable Policy</Text>

        {error && <Banner tone="critical" onDismiss={() => setError(null)}><Text as="p">{error}</Text></Banner>}

        <BlockStack gap="300">
          <TextField
            label="Number of retry days"
            type="number"
            value={String(config.days)}
            onChange={v => {
              const n = parseInt(v, 10)
              if (!isNaN(n) && n >= 1 && n <= 14) syncDaysCount(n)
            }}
            min={1}
            max={14}
            autoComplete="off"
            helpText="How many days to retry before considering the customer unreachable (1–14)."
          />

          <BlockStack gap="200">
            <Text as="p" variant="bodyMd" fontWeight="semibold">Attempts per day</Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Set how many call attempts to make on each retry day.
            </Text>
            {config.attemptsPerDay.map((attempts, i) => (
              <InlineStack key={i} gap="300" blockAlign="center">
                <Text as="p" variant="bodySm" tone="subdued" alignment="end">Day {i + 1}</Text>
                <div style={{ width: '80px' }}>
                  <TextField
                    label={`Day ${i + 1} attempts`}
                    labelHidden
                    type="number"
                    value={String(attempts)}
                    onChange={v => updateAttemptsPerDay(i, v)}
                    min={0}
                    max={20}
                    autoComplete="off"
                  />
                </div>
              </InlineStack>
            ))}
          </BlockStack>

          <Select
            label="After exhaustion"
            options={EXHAUSTION_OPTIONS}
            value={config.afterExhaustion}
            onChange={v => setConfig(prev => ({ ...prev, afterExhaustion: v as UnreachableConfig['afterExhaustion'] }))}
            helpText="What to do with the order once all retry attempts are exhausted."
          />
        </BlockStack>

      </BlockStack>
    </Card>
  )
})
